import { Injectable, inject } from '@angular/core';
import { PhoneBookRecipientRequest } from 'src/app/core/api/models';
import {
  DialogService,
  DialogStatus,
} from 'src/app/core/services/dialog.service';
import { cloneDeepObj } from 'src/app/core/utils/util';
import { PhonebookCustomDto } from './phonebook-custom.model';

@Injectable({
  providedIn: 'root',
})
export class PhonebookStateService {
  dialogService = inject(DialogService);

  /** مقدار یک فیلد خاص را در کل آیتم ها و زیرمجموعه ها اعمال میکنه */
  deepChangePhonebooksState(
    phonebooks: PhonebookCustomDto[],
    key: string,
    value: any
  ) {
    const changeProp = (list: PhonebookCustomDto[]) => {
      list.forEach((item) => {
        item[key] = value;
        if (item.subItems?.length) changeProp(item.subItems);
      });
    };
    changeProp(phonebooks);
    return phonebooks;
  }

  /** آپدیت کردن لیست گیرندگان با حفظ وضعیت قبلی */
  updateSelectedRecipients(
    phonebooks: PhonebookCustomDto[],
    selectedRecipients?: PhonebookCustomDto[]
  ): PhonebookCustomDto[] {
    if (!selectedRecipients) selectedRecipients = [];

    phonebooks.forEach((phonebook) => {
      const { checked, fromIndex, count, id, isValid } = phonebook;
      const selectedIndex = selectedRecipients?.findIndex((p) => p.id == id);
      // قبلا وجود داشته
      if (checked && selectedIndex > -1) {
        selectedRecipients[selectedIndex].fromIndex = fromIndex;
        selectedRecipients[selectedIndex].count = count;
        selectedRecipients[selectedIndex].isValid = isValid;
      }
      // تازه اومده
      else if (checked && selectedIndex == -1)
        selectedRecipients.push(phonebook);
      // وجود داشته باید حذف بشه
      else if (!checked && selectedIndex > -1)
        selectedRecipients.splice(selectedIndex, 1);
    });

    return selectedRecipients;
  }

  getPhoneBookById(phonebooks: PhonebookCustomDto[], id: number) {
    let selectedPhonebook: PhonebookCustomDto = null;
    const getSelectedPhonebook = (list: PhonebookCustomDto[]) => {
      list.forEach((item) => {
        if (item?.id === id) selectedPhonebook = item;
        if (item.subItems?.length && !selectedPhonebook)
          getSelectedPhonebook(item.subItems);
      });
    };
    getSelectedPhonebook(phonebooks);
    return selectedPhonebook;
  }

  /** جهت آپدیت کردن مقدار یک فیلد خاص یک دفترچه تلفن خاص */
  updatePhonebook(
    phonebooks: PhonebookCustomDto[],
    id: number,
    key: string,
    value: any
  ) {
    let selectedPhonebook: PhonebookCustomDto = this.getPhoneBookById(
      phonebooks,
      id
    );
    if (selectedPhonebook && key) selectedPhonebook[key] = value;
  }

  /** آیا لیست گیرندگانی که انتخاب شده اند اندیس گذاری هاشون صحیح می باشد یا خیر ؟ */
  isValidSelectedRecipients(phonebooks: PhonebookCustomDto[]): boolean {
    return phonebooks?.every((p) => p?.isValid as boolean);
  }

  /**آیا همه ساب ایتم ها انتخاب شده اند یا خیر (فقط لول 1 و فارغ از اینکه اندیس گذاری شده اند یا نه ) ؟ */
  isCheckedAllSubItems(phonebooks: PhonebookCustomDto[]): boolean {
    if (!phonebooks?.length || !phonebooks) return false;
    return phonebooks?.every((p) => p.checked === true);
  }

  /**آیا بعضی از ساب ایتم ها انتخاب شده اند یا خیر (فقط لول 1 و فارغ از اینکه اندیس گذاری شده اند یا نه ) ؟ */
  isCheckedSomeSubItems(phonebooks: PhonebookCustomDto[]): boolean {
    if (!phonebooks?.length || !phonebooks) return false;
    if (this.isCheckedAllSubItems(phonebooks)) return false;
    return phonebooks.some((p) => p.checked === true);
  }

  /**انتخاب تمامی دفترتلفن ها به همراه زیرمجموعه ها بصورت تودرتو ؟ */
  selectAll(phonebooks: PhonebookCustomDto[], checked: boolean) {
    const findAndSelect = (phonebooks: PhonebookCustomDto[]) => {
      phonebooks.forEach((p) => {
        p.checked = checked;
        p.fromIndex = checked && p.contactsCount ? p.fromIndex || 1 : undefined;
        p.count = checked ? p.count || p.contactsCount : undefined;
        p.isValid = this.isValidPhonebook(p);
        if (p.subItems?.length) findAndSelect(p.subItems);
      });
    };
    findAndSelect(phonebooks);
  }

  fixPhonebooks(
    phonebookRecipients: PhonebookCustomDto[]
  ): PhoneBookRecipientRequest[] {
    return cloneDeepObj(
      phonebookRecipients
        .filter((p) => p.fromIndex && p.count)
        .map((p) => ({
          phonebookId: p.id,
          count: p.count,
          fromIndex: p.fromIndex,
        }))
    );
  }

  /** جهت اعمال اخرین وضعیت لیست (برای سرچ و موقعی که لیست دفترچه تلفن ها تغییر میکنه کاربرد داره که انتخاب شده های قبلی را اعمال کنه) */
  applySelectedPhonebooks(
    phonebooks: PhonebookCustomDto[],
    phonebookRecipients: PhonebookCustomDto[]
  ) {
    const findAndSelect = (phonebooks: PhonebookCustomDto[]) => {
      phonebooks?.forEach((item) => {
        const selectedPhonebook = phonebookRecipients?.find(
          (p) => p.id == item.id
        );

        if (selectedPhonebook) {
          item.checked = true;
          item.fromIndex = selectedPhonebook.fromIndex;
          item.count = selectedPhonebook.count;
          item.isValid = this.isValidPhonebook(selectedPhonebook);
        }
        if (item.subItems?.length) findAndSelect(item.subItems);
      });
    };
    findAndSelect(phonebooks);
  }

  checkForEmptyRecipients(
    phoneBookRecipient,
    emptyMessage: string = 'emptyReceiversOrWrongIndex'
  ): boolean {
    if (!phoneBookRecipient || phoneBookRecipient?.length == 0) {
      this.emptyPhonebookRecipientError(emptyMessage);
      return false;
    }
    return true;
  }

  isValidPhonebook(phonebook: PhonebookCustomDto): boolean {
    const { fromIndex, count, contactsCount } = phonebook;
    if (count === undefined && fromIndex === undefined) return true;
    if (!contactsCount) return true;
    if (+count <= 0 || +fromIndex <= 0) return false;
    if (count > contactsCount) return false;
    if (count + fromIndex > contactsCount + 1) return false;
    return true;
  }

  emptyPhonebookRecipientError(emptyMessage) {
    this.dialogService.openDialog({
      title: 'common.error',
      message: 'common.' + emptyMessage,
      status: DialogStatus.error,
    });
  }
}
